/*
 * Pure @mention helpers for the feed composer and comments.
 * Mention entry shape (matches backend validation): { id, uuidv4, name }.
 * No lookbehind regexes (iOS Safari). Module-level constants only — never mutated.
 */

export const MAX_MENTIONS = 10;

// Find an active "@query" immediately before the caret.
// The query may contain at most ONE space (typing "Ola No..."), so a completed
// mention followed by further typing ("@Ola Nordmann h") stops triggering.
export function detectMentionQuery(text, caretPos) {
    const before = String(text || '').slice(0, caretPos || 0);
    const atIndex = before.lastIndexOf('@');
    if (atIndex === -1) {
        return null;
    }
    if (atIndex > 0 && !/\s/.test(before[atIndex - 1])) {
        return null; // '@' glued to a word (e.g. emails) is not a mention trigger
    }
    const query = before.slice(atIndex + 1);
    if (query.includes('\n') || query.includes('@') || query.length > 30) {
        return null;
    }
    if ((query.match(/ /g) || []).length > 1) {
        return null;
    }
    return { query, startIndex: atIndex };
}

// Replace the active "@query" (from startIndex to caretPos) with "@Name ".
export function insertMention(text, startIndex, caretPos, name) {
    const source = String(text || '');
    const mentionText = `@${name} `;
    const newText = source.slice(0, startIndex) + mentionText + source.slice(caretPos);
    return { newText, newCaret: startIndex + mentionText.length };
}

const escapeRegExp = s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Replace each stored mention's "@Name" with a markdown profile link.
// Longest names first so "@Ola" never swallows "@Ola Nordmann"; the leading
// `(^|[^\[])` guard keeps shorter names from re-matching inside the "[@Long
// Name](...)" text a previous pass produced (no lookbehind — iOS Safari).
export function linkifyMentions(text, mentions) {
    if (!text || !Array.isArray(mentions) || mentions.length === 0) {
        return text;
    }
    let result = text;
    const sorted = mentions
        .filter(m => m && m.name && m.uuidv4)
        .slice()
        .sort((a, b) => b.name.length - a.name.length);
    sorted.forEach(m => {
        const pattern = new RegExp(`(^|[^\\[])@${escapeRegExp(m.name)}`, 'g');
        result = result.replace(pattern, `$1[@${m.name}](/users/profile/view/${m.uuidv4})`);
    });
    return result;
}

// Keep only mentions whose "@Name" still occurs in the text (the user may have
// deleted a mention after picking it). Dedupes by id. Run before save.
export function extractValidMentions(text, mentions) {
    if (!text || !Array.isArray(mentions)) {
        return [];
    }
    const seen = new Set();
    return mentions.filter(m => {
        if (!m || !m.id || !m.name) {
            return false;
        }
        if (seen.has(m.id)) {
            return false;
        }
        if (!text.includes(`@${m.name}`)) {
            return false;
        }
        seen.add(m.id);
        return true;
    });
}
